import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, Drawer, IconButton, List, ListItem, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { listVersions } from '../../utilityFunctions/dashboardApi';

const formatTs = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(lang && lang.startsWith('es') ? 'es-CO' : 'en-US',
      { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return iso;
  }
};

/**
 * Version history. "View" loads a past version read-only; "Revert" asks the
 * server to create a NEW version with that spec (history is never rewritten).
 */
export default function VersionsDrawer({
  open, onClose, dashboardId, jwt, currentVersionId, viewingVersionId, canEdit, onView, onRevert, refreshKey,
}) {
  const { t, i18n } = useTranslation();
  const [versions, setVersions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [confirm, setConfirm] = React.useState(null);
  const [reverting, setReverting] = React.useState(false);

  React.useEffect(() => {
    if (!open || !dashboardId) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    listVersions(dashboardId, jwt).then((res) => {
      if (!alive) return;
      setLoading(false);
      if (res.ok && Array.isArray(res.data)) {
        setVersions(res.data.slice().sort((a, b) => (b.version_no || 0) - (a.version_no || 0)));
      } else {
        setError(t('Dashboards.versions.loadFailed'));
      }
    });
    return () => { alive = false; };
  }, [open, dashboardId, jwt, refreshKey, t]);

  const doRevert = async () => {
    const v = confirm;
    setReverting(true);
    await onRevert(v.id);
    setReverting(false);
    setConfirm(null);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, maxWidth: '100%', overflowWrap: 'anywhere' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>{t('Dashboards.versions.title')}</Typography>
        <IconButton onClick={onClose} aria-label={t('Dashboards.common.close')}><CloseIcon /></IconButton>
      </Box>
      <Divider />
      {loading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
      {!loading && !error && versions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('Dashboards.versions.empty')}</Typography>
      )}
      <List dense sx={{ overflow: 'auto' }}>
        {versions.map((v) => {
          const isCurrent = String(v.id) === String(currentVersionId);
          const isViewing = String(v.id) === String(viewingVersionId);
          return (
            <ListItem key={v.id} divider sx={{ display: 'block', bgcolor: isViewing ? 'action.selected' : 'transparent' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t('Dashboards.versions.versionNo', { n: v.version_no })}
                </Typography>
                {isCurrent && <Chip size="small" color="primary" label={t('Dashboards.versions.current')} />}
                {v.input_kind && (
                  <Chip size="small" variant="outlined"
                    label={t(`Dashboards.versions.inputKinds.${v.input_kind}`, { defaultValue: v.input_kind })} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">{formatTs(v.created_at, i18n.language)}</Typography>
              {v.change_summary && <Typography variant="body2" sx={{ mt: 0.5 }}>{v.change_summary}</Typography>}
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Button size="small" disabled={isViewing} onClick={() => onView(isCurrent ? null : v.id)}>
                  {isCurrent ? t('Dashboards.versions.viewCurrent') : t('Dashboards.versions.view')}
                </Button>
                {canEdit && !isCurrent && (
                  <Button size="small" color="warning" onClick={() => setConfirm(v)}>{t('Dashboards.versions.revert')}</Button>
                )}
              </Box>
            </ListItem>
          );
        })}
      </List>

      <Dialog open={!!confirm} onClose={() => !reverting && setConfirm(null)}>
        <DialogTitle>{t('Dashboards.versions.revertTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm && t('Dashboards.versions.revertBody', { n: confirm.version_no })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={reverting} onClick={() => setConfirm(null)}>{t('Dashboards.common.cancel')}</Button>
          <Button variant="contained" disabled={reverting} onClick={doRevert}>
            {reverting ? <CircularProgress size={18} /> : t('Dashboards.versions.revert')}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
